import { Header } from "components/atoms/Typography";
import { IncreaseDecreasePercentage } from "components/molecules/Card/CardFunctions";
import ChartContainer from "components/molecules/ChartContainer";
import { AreaChart } from "components/molecules/Charts";
import { AreaChartDatum } from "components/molecules/Charts/AreaChart";
import { ChartNoDataState } from "components/molecules/NoDataState";
import { DATA_KEY_COLORS } from "constants/chartsColors.constant";
import { useMemo } from "react";
import usePlansState from "recoilStore/Building/usePlansState";
import { CURRENT_YEAR, END_YEAR } from "utils/constants";
import { getDisplayInfo } from "utils/formatting";
import { prepareAnnualCarbonEmissionsOutcomes } from "./AnnualCarbonEmissionsOutcomes.helper";

interface AnnualCarbonEmissionsOutcomesProps {
    backgroundColor?: string;
}

function AnnualCarbonEmissionsOutcomes({ backgroundColor }: AnnualCarbonEmissionsOutcomesProps) {
    const { referencePlan, comparedPlan, referencePlanName, comparedPlanName, referencedPlanId, comparedPlanId } =
        usePlansState();
    const {
        chartAnnualCarbonEmissionsData: data,
        carbonEmissionsDifference,
        tooltipElement,
        formatedCarbonEmissionsRef,
    } = prepareAnnualCarbonEmissionsOutcomes(referencePlan, comparedPlan, referencePlanName, comparedPlanName);

    const dataWithCalculatedGoalLine = useMemo((): AreaChartDatum[] => {
        if (!data || data?.length === 0) return [];

        // calculate goal line (from current year emissions to zero emissions in 2050)
        const y1 = 0;
        const y0 = data.find(({ year }) => year === CURRENT_YEAR)?.totalCarbonEmissionsReference || 0;
        const m = (y1 - y0) / (END_YEAR - CURRENT_YEAR);
        const c = 0 - m * END_YEAR;

        const baselineData: { x: number; y: number }[] = [];
        const goalData: { x: number; y: number }[] = [];
        const recommendationsData: { x: number; y: number }[] = [];

        data.forEach(({ year: x, totalCarbonEmissionsCompared, totalCarbonEmissionsReference }) => {
            baselineData.push({
                x,
                y: totalCarbonEmissionsCompared || 0,
            });
            recommendationsData.push({
                x,
                y: totalCarbonEmissionsReference || 0,
            });
            goalData.push({
                x,
                y: m * x + c,
            });
        });
        return [
            {
                type: "line" as const,
                id: referencePlanName,
                color: DATA_KEY_COLORS.recommendations,
                data: recommendationsData,
                formatKey: "annualCarbonEmissionsCurrent",
            },
            {
                type: "line" as const,
                id: "Goal",
                color: DATA_KEY_COLORS.goal,
                data: goalData,
                formatKey: "annualCarbonEmissionsCurrent",
            },
            ...(comparedPlanId !== referencedPlanId
                ? [
                      {
                          type: "line" as const,
                          id: comparedPlanName,
                          color: DATA_KEY_COLORS.baseline,
                          data: baselineData,
                          formatKey: "annualCarbonEmissionsCurrent",
                      },
                  ]
                : []),
        ];
    }, [data]);

    const { unit, formatFunction } = useMemo(
        () =>
            getDisplayInfo(
                "annualCarbonEmissionsCurrent",
                dataWithCalculatedGoalLine.length > 0 ? dataWithCalculatedGoalLine[1].data.map(({ y }) => y) : 0
            ),
        [dataWithCalculatedGoalLine]
    );

    if (data === null) return <ChartNoDataState title="Annual Carbon Emissions" />;

    return (
        <ChartContainer title="Emissions" exportableData={data}>
            <div className="card__value-and-percentage" style={{ justifyContent: "space-between", width: "70%" }}>
                <Header>
                    {/* <Header style={{ color: valueColor }}> */}
                    {formatedCarbonEmissionsRef} {unit}
                </Header>
                <IncreaseDecreasePercentage
                    isHeader={true}
                    value={carbonEmissionsDifference}
                    tooltipElement={tooltipElement}
                />
            </div>
            <AreaChart
                data={dataWithCalculatedGoalLine}
                yAxisInfo={{
                    formatFunction: (v: string | number) => formatFunction(v, false),
                    unit: unit || "",
                }}
                backgroundColor={backgroundColor}
                enableGridX={true}
                height={350}
            />
        </ChartContainer>
    );
}

export default AnnualCarbonEmissionsOutcomes;
